import i18n from "../i18n";

/** Plain-English review status helpers.
 *
 * The 360 review state machine has eleven internal statuses; nobody should
 * have to read "colleague_feedback_in_progress" to know what is going on.
 * Everything that shows a review status should go through these helpers so
 * the wording stays consistent across the admin panel, My Team and the
 * employee-facing Performance page.
 */

/** The five stages a review moves through, in order. */
export const REVIEW_STAGES = [
  "self",
  "peers",
  "manager",
  "meeting",
  "done",
] as const;
export type ReviewStage = (typeof REVIEW_STAGES)[number];

interface StatusMeta {
  /** i18n key suffix under reviews.status.* */
  key: string;
  /** Fallback label (en-GB). */
  label: string;
  /** Which stage of the journey this status sits in. */
  stage: ReviewStage | null;
  /** Badge colour classes. */
  tone: string;
  /** Fallback one-liner: what is happening / who acts next. */
  next: string;
}

const STATUS_META: Record<string, StatusMeta> = {
  not_started: {
    key: "notStarted",
    label: "Not started",
    stage: "self",
    tone: "bg-gray-100 text-gray-600",
    next: "Waiting for the employee to begin their self-assessment.",
  },
  self_assessment_in_progress: {
    key: "selfInProgress",
    label: "Self-assessment",
    stage: "self",
    tone: "bg-blue-50 text-blue-700",
    next: "The employee is writing their self-assessment.",
  },
  self_assessment_submitted: {
    key: "selfSubmitted",
    label: "Self-assessment done",
    stage: "peers",
    tone: "bg-blue-50 text-blue-700",
    next: "Self-assessment is in. Choose colleagues for feedback, or skip to the manager review.",
  },
  colleague_feedback_in_progress: {
    key: "peersInProgress",
    label: "Waiting on colleagues",
    stage: "peers",
    tone: "bg-indigo-50 text-indigo-700",
    next: "Colleagues are giving their feedback. The manager can start drafting.",
  },
  colleague_feedback_complete: {
    key: "peersComplete",
    label: "Ready for manager review",
    stage: "manager",
    tone: "bg-amber-50 text-amber-800",
    next: "All colleague feedback is in — the manager review can start.",
  },
  manager_review_in_progress: {
    key: "managerInProgress",
    label: "Manager review",
    stage: "manager",
    tone: "bg-amber-50 text-amber-800",
    next: "The manager is writing their review.",
  },
  manager_review_submitted: {
    key: "managerSubmitted",
    label: "Ready for review meeting",
    stage: "meeting",
    tone: "bg-purple-50 text-purple-700",
    next: "Manager review done. Next: book the review meeting.",
  },
  meeting_pending: {
    key: "meetingPending",
    label: "Review meeting",
    stage: "meeting",
    tone: "bg-purple-50 text-purple-700",
    next: "Meeting to hold. Go through it together with the meeting guide, then finish the appraisal there.",
  },
  employee_response_pending: {
    key: "responsePending",
    label: "Awaiting employee response",
    stage: "meeting",
    tone: "bg-purple-50 text-purple-700",
    next: "Waiting for the employee to add their response.",
  },
  completed: {
    key: "completed",
    label: "Completed",
    stage: "done",
    tone: "bg-green-50 text-green-700",
    next: "This review is complete.",
  },
  cancelled: {
    key: "cancelled",
    label: "Cancelled",
    stage: null,
    tone: "bg-gray-100 text-gray-400",
    next: "This review was cancelled.",
  },
};

function meta(status: string): StatusMeta {
  return (
    STATUS_META[status] ?? {
      key: status,
      label: status.replace(/_/g, " "),
      stage: null,
      tone: "bg-gray-100 text-gray-600",
      next: "",
    }
  );
}

/** Friendly label, e.g. "Waiting on colleagues". */
export function reviewStatusLabel(status: string): string {
  const m = meta(status);
  return i18n.t(`reviews.status.${m.key}`, m.label);
}

/** One line on what happens next / who acts. */
export function reviewStatusNext(status: string): string {
  const m = meta(status);
  if (!m.next) return "";
  return i18n.t(`reviews.statusNext.${m.key}`, m.next);
}

/** Badge colour classes for a status. */
export function reviewStatusTone(status: string): string {
  return meta(status).tone;
}

/** Index of the review's current stage (0–4), or -1 for cancelled/unknown. */
export function reviewStageIndex(status: string): number {
  const stage = meta(status).stage;
  return stage ? REVIEW_STAGES.indexOf(stage) : -1;
}

/** Short labels for the five stage pips. */
export function reviewStageLabels(): string[] {
  return [
    i18n.t("reviews.stage.self", "Self"),
    i18n.t("reviews.stage.peers", "Colleagues"),
    i18n.t("reviews.stage.manager", "Manager"),
    i18n.t("reviews.stage.meeting", "Meeting"),
    i18n.t("reviews.stage.done", "Done"),
  ];
}
